/**
 * Breadcrumb Navigation Utilities
 *
 * This module provides utilities for building dynamic breadcrumbs that preserve
 * navigation history. When navigating between entities (Contact → Client Group,
 * Account → Contact, etc.), the breadcrumb trail should reflect the actual path taken.
 *
 * Usage:
 * 1. When creating a link to another entity, use `buildNavigationUrl()` to include referrer info
 * 2. In the target page, use `buildBreadcrumbsFromUrl()` to construct dynamic breadcrumbs
 *
 * Example:
 * ```tsx
 * // In Contact Detail - linking to Client Group
 * const url = buildNavigationUrl(`/client-groups/${groupId}`, {
 *   from: 'contact',
 *   fromId: contactId,
 *   fromLabel: fullName
 * });
 * navigate(url);
 *
 * // In Client Group Detail - build breadcrumbs
 * const searchParams = new URLSearchParams(location.search);
 * const breadcrumbs = buildBreadcrumbsFromUrl(
 *   searchParams,
 *   [
 *     { label: "Home", href: "/" },
 *     { label: "Client Groups", href: "/client-groups" },
 *     { label: group.groupName },
 *   ]
 * );
 * ```
 */

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

/** Allowed entity types for breadcrumb navigation context */
export const VALID_FROM_VALUES = ['contact', 'account', 'client-group', 'partner', 'deal'] as const;
export type FromValue = (typeof VALID_FROM_VALUES)[number];

export interface NavigationContext {
  from: FromValue;
  fromId: string | number;
  fromLabel: string;
}

/**
 * Build a navigation URL with referrer context encoded as query parameters.
 * This allows the target page to construct breadcrumbs showing the navigation history.
 *
 * @param targetUrl - The destination URL (e.g., "/client-groups/123")
 * @param context - Navigation context (where we're coming from)
 * @returns URL with query parameters encoding the navigation history
 */
export function buildNavigationUrl(
  targetUrl: string,
  context: NavigationContext
): string {
  try {
    const url = new URL(targetUrl, window.location.origin);
    url.searchParams.set('from', context.from);
    url.searchParams.set('fromId', String(context.fromId));
    url.searchParams.set('fromLabel', context.fromLabel);
    return `${url.pathname}${url.search}`;
  } catch {
    console.warn('[breadcrumbs] Invalid URL passed to buildNavigationUrl:', targetUrl);
    return targetUrl;
  }
}

/**
 * Build breadcrumbs from URL search parameters, inserting referrer information
 * into the breadcrumb trail if present.
 *
 * @param searchParams - URL search parameters (from `new URLSearchParams(location.search)`)
 * @param defaultBreadcrumbs - Default breadcrumb trail (used when no referrer context)
 * @returns Breadcrumb trail with referrer inserted if present
 */
export function buildBreadcrumbsFromUrl(
  searchParams: URLSearchParams,
  defaultBreadcrumbs: BreadcrumbItem[]
): BreadcrumbItem[] {
  const from = searchParams.get('from');
  const fromId = searchParams.get('fromId');
  const fromLabel = searchParams.get('fromLabel');

  // If no referrer context, return default breadcrumbs
  if (!from || !fromId || !fromLabel?.trim()) {
    return defaultBreadcrumbs;
  }

  // Validate from against allowlist (reject unknown values)
  if (!(VALID_FROM_VALUES as readonly string[]).includes(from)) {
    return defaultBreadcrumbs;
  }

  // Sanitise fromId — strip path traversal sequences
  const safeFromId = fromId.replace(/\.\.\//g, '').replace(/[/\\]/g, '');

  // Build referrer breadcrumb item
  const referrerItem: BreadcrumbItem = {
    label: fromLabel.trim(),
    href: getReferrerUrl(from, safeFromId),
  };

  // Insert referrer into breadcrumb trail
  // Structure: Home > [Parent List] > [Referrer Entity] > Current Page
  const breadcrumbs: BreadcrumbItem[] = [
    { label: "Home", href: "/" },
  ];

  // Add parent list page for the referrer
  const parentList = getParentListPage(from);
  if (parentList) {
    breadcrumbs.push(parentList);
  }

  // Add the referrer entity
  breadcrumbs.push(referrerItem);

  // Add the current page (last item from defaultBreadcrumbs)
  const currentPage = defaultBreadcrumbs[defaultBreadcrumbs.length - 1];
  breadcrumbs.push(currentPage);

  return breadcrumbs;
}

/**
 * Get the URL for a referrer entity.
 *
 * @param from - Entity type (contact, account, etc.)
 * @param fromId - Entity ID
 * @returns URL to the referrer entity's detail page
 */
function getReferrerUrl(from: string, fromId: string): string {
  const routes: Record<string, string> = {
    contact: '/contacts',
    account: '/accounts',
    'client-group': '/client-groups',
    partner: '/partners',
    deal: '/deals',
  };

  const basePath = routes[from];
  return basePath ? `${basePath}/${fromId}` : '/';
}

/**
 * Get the parent list page breadcrumb for an entity type.
 *
 * @param from - Entity type
 * @returns Breadcrumb item for the parent list page
 */
function getParentListPage(from: string): BreadcrumbItem | null {
  const pages: Record<string, BreadcrumbItem> = {
    contact: { label: 'Contacts', href: '/contacts' },
    account: { label: 'Accounts', href: '/accounts' },
    'client-group': { label: 'Client Groups', href: '/client-groups' },
    partner: { label: 'Partners', href: '/partners' },
    deal: { label: 'Deals', href: '/deals' },
  };

  return pages[from] || null;
}

/**
 * Extract navigation context from the current URL.
 * Useful when building links from the current page to another entity.
 *
 * @param location - Location string from useLocation() hook
 * @returns Navigation context if present, null otherwise
 */
export function getNavigationContext(location: string): NavigationContext | null {
  const url = new URL(location, window.location.origin);
  const searchParams = url.searchParams;

  const from = searchParams.get('from');
  const fromId = searchParams.get('fromId');
  const fromLabel = searchParams.get('fromLabel');

  if (from && fromId && fromLabel?.trim() && (VALID_FROM_VALUES as readonly string[]).includes(from)) {
    const safeFromId = fromId.replace(/\.\.\//g, '').replace(/[/\\]/g, '');
    return {
      from: from as FromValue,
      fromId: safeFromId,
      fromLabel: fromLabel.trim(),
    };
  }

  return null;
}
